import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dimmer,
  Loader,
  Message,
  Modal,
  Table
} from 'semantic-ui-react';
import { BondInsuranceAnalysisForGetBondInsurancesResponseDTO } from '../../../dtos/bond-insurance-list/get-bond-insurances-response.dto';
import { BondInsuranceAnalysisStatusEnum } from '../../../enums/bond-insurance-analysis-status.enum';
import Input from '../../../components/Input';
import { unexpireBondInsurance } from '../../../services/bond-insurance';
import { dangerNotification, successNotification } from '../../../services/notification';

type AnalysisSelection = {
  analysisId: string;
  insurerName: string;
  status: string;
  selected: boolean;
  expiredAt: string;
  selectable: boolean;
};

type Props = {
  open: boolean;
  onClose: () => void;
  bondInsuranceId: string;
  analyses: BondInsuranceAnalysisForGetBondInsurancesResponseDTO[];
  onSuccess: () => Promise<void> | void;
};

const selectableStatuses = [
  BondInsuranceAnalysisStatusEnum.approved,
  BondInsuranceAnalysisStatusEnum.biometry
];

const getDefaultExpirationDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 1);
  return date.toISOString().split('T')[0];
};

const getStatusLabel = (status: string): string => {
  switch (status) {
    case BondInsuranceAnalysisStatusEnum.approved:
      return 'APROVADA';
    case BondInsuranceAnalysisStatusEnum.biometry:
      return 'BIOMETRIA';
    case BondInsuranceAnalysisStatusEnum.rejected:
      return 'REJEITADA';
    case BondInsuranceAnalysisStatusEnum.pending:
      return 'PENDENCIADA';
    case BondInsuranceAnalysisStatusEnum.proposal:
      return 'EM PROPOSTA';
    case BondInsuranceAnalysisStatusEnum.analysis:
      return 'EM ANÁLISE';
    case BondInsuranceAnalysisStatusEnum.waiting:
      return 'EM ESPERA';
    default:
      return status;
  }
};

const buildInitialSelections = (
  analyses: BondInsuranceAnalysisForGetBondInsurancesResponseDTO[]
): AnalysisSelection[] => {
  const defaultDate = getDefaultExpirationDate();

  return analyses.map(analysis => {
    const selectable = selectableStatuses.includes(
      analysis.status as BondInsuranceAnalysisStatusEnum
    );

    return {
      analysisId: analysis.id,
      insurerName: analysis.insurer?.name || 'Seguradora',
      status: analysis.status,
      selected: selectable,
      expiredAt: defaultDate,
      selectable
    };
  });
};

export const UnexpireBondInsuranceModal = ({
  open,
  onClose,
  bondInsuranceId,
  analyses,
  onSuccess
}: Props): JSX.Element => {
  const [selections, setSelections] = useState<AnalysisSelection[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSelections(buildInitialSelections(analyses));
    }
  }, [open, analyses]);

  const selectedAnalyses = useMemo(
    () => selections.filter(selection => selection.selected),
    [selections]
  );

  const handleToggle = (analysisId: string, checked: boolean) => {
    setSelections(prev =>
      prev.map(selection =>
        selection.analysisId === analysisId
          ? { ...selection, selected: checked }
          : selection
      )
    );
  };

  const handleDateChange = (analysisId: string, expiredAt: string) => {
    setSelections(prev =>
      prev.map(selection =>
        selection.analysisId === analysisId
          ? { ...selection, expiredAt }
          : selection
      )
    );
  };

  const handleConfirm = async () => {
    if (!selectedAnalyses.length) {
      dangerNotification('Atenção', 'Selecione ao menos uma análise para reativar.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];

    const hasInvalidDate = selectedAnalyses.some(selection => {
      return !selection.expiredAt || selection.expiredAt <= today;
    });

    if (hasInvalidDate) {
      dangerNotification(
        'Atenção',
        'A nova data de expiração deve ser posterior à data atual.'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await unexpireBondInsurance(
        bondInsuranceId,
        selectedAnalyses.map(selection => ({
          analysisId: selection.analysisId,
          expiredAt: selection.expiredAt
        }))
      );

      if (response?.success) {
        successNotification('Sucesso!', response.message || 'Análise reativada com sucesso.');
        onClose();
        await onSuccess();
      } else {
        dangerNotification('Erro', 'Não foi possível reativar a análise.');
      }
    } catch (error) {
      const message =
        (error as any)?.response?.data?.message ||
        (error as Error)?.message ||
        'Não foi possível reativar a análise.';
      dangerNotification('Erro', message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header>Reativar análise expirada</Modal.Header>
      <Modal.Content>
        <Dimmer active={isSubmitting} inverted>
          <Loader />
        </Dimmer>

        <p>
          A análise voltará para <strong>Gerenciar Análises</strong> e poderá ser
          contratada pelas seguradoras selecionadas abaixo.
        </p>

        <Message info>
          <Message.Header>Validade das análises</Message.Header>
          <p>
            As seguradoras selecionadas ficarão ativas até a data definida. As
            demais permanecerão expiradas. A análise só voltará para Expirados
            quando todas as análises individuais estiverem vencidas.
          </p>
        </Message>

        <Table celled compact>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>REATIVAR</Table.HeaderCell>
              <Table.HeaderCell>SEGURADORA</Table.HeaderCell>
              <Table.HeaderCell>STATUS</Table.HeaderCell>
              <Table.HeaderCell>NOVA DATA DE EXPIRAÇÃO</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {selections.map(selection => (
              <Table.Row key={selection.analysisId}>
                <Table.Cell collapsing>
                  <Checkbox
                    checked={selection.selected}
                    disabled={!selection.selectable}
                    onChange={(_, data) =>
                      handleToggle(selection.analysisId, Boolean(data.checked))
                    }
                  />
                </Table.Cell>
                <Table.Cell>{selection.insurerName}</Table.Cell>
                <Table.Cell>{getStatusLabel(selection.status)}</Table.Cell>
                <Table.Cell>
                  {selection.selected ? (
                    <Input
                      type="date"
                      name={`expiredAt-${selection.analysisId}`}
                      value={selection.expiredAt}
                      min={getDefaultExpirationDate()}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        handleDateChange(selection.analysisId, event.target.value)
                      }
                      fluid
                    />
                  ) : (
                    <span style={{ color: '#999' }}>Permanecerá expirada</span>
                  )}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button primary onClick={handleConfirm} loading={isSubmitting} disabled={isSubmitting}>
          Confirmar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
